import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, FileText, GraduationCap, User, MapPin, School, CreditCard, AlertCircle, ArrowRight } from 'lucide-react';
import Header from '../common/Header';
import { useApplicationFee } from '../../hooks/useApplicationFee';

const ApplicationReview = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [applicationData, setApplicationData] = useState(null);

  // Same NEET-Leaders-batch detection/fee logic used on the payment page —
  // refreshes Zoho data itself so this doesn't show a stale login-time grade.
  const { applicationFeeDisplay } = useApplicationFee();

  useEffect(() => {
    loadApplicationData();
  }, []);

  const loadApplicationData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/application/details', {
        headers: { 'x-auth-token': token }
      });

      if (response.ok) {
        const data = await response.json();
        setApplicationData(data.application);
      } else {
        setError('Failed to load application data');
      }
    } catch (error) {
      setError('Failed to load application data');
    }
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/application/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        }
      });

      if (response.ok) {
        setSuccess('Application submitted successfully!');
        setTimeout(() => {
          navigate('/dashboard');
        }, 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to submit application');
      }
    } catch (error) {
      setError('Failed to submit application. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!applicationData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Application Review" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-8">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 rounded-full p-3 mr-4">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Application Review</h1>
                <p className="text-teal-100 mt-2">Review your application before final submission</p>
              </div>
            </div>
          </div>

          {/* Review Content */}
          <div className="px-6 py-8">
            {/* Success/Error Messages */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-5 w-5 text-red-600" />
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-5 w-5 text-green-600" />
                  <p className="text-green-800 text-sm">{success}</p>
                </div>
              </div>
            )}

            {/* Application Summary */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Stage 1 Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <FileText className="h-6 w-6 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Stage 1: Basic Information</h3>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                
                {applicationData.stage1 && (
                  <div className="space-y-3 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Campus:</span>
                      <span className="font-medium text-gray-900">{applicationData.stage1.preferredCampus}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Session:</span>
                      <span className="font-medium text-gray-900">{applicationData.stage1.session}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Grade:</span>
                      <span className="font-medium text-gray-900">{applicationData.stage1.gradeApplied}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Student Name:</span>
                      <span className="font-medium text-gray-900">
                        {applicationData.stage1.studentFirstName} {applicationData.stage1.studentLastName}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium text-gray-900">{applicationData.stage1.email}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Payment Summary */}
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <CreditCard className="h-6 w-6 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Payment Status</h3>
                  {applicationData.payment?.status === 'completed' ? (
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  ) : (
                    <div className="h-5 w-5 rounded-full border-2 border-gray-300"></div>
                  )}
                </div>
                
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className={`font-medium px-2 py-1 rounded-full text-xs ${
                      applicationData.payment?.status === 'completed' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-yellow-100 text-yellow-800'
                    }`}>
                      {applicationData.payment?.status === 'completed' ? 'Completed' : 'Pending'}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount:</span>
                    <span className="font-medium text-gray-900">{applicationFeeDisplay}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Stage 2 Summary */}
            {applicationData.stage2 && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <div className="flex items-center space-x-3 mb-4">
                  <FileText className="h-6 w-6 text-teal-600" />
                  <h3 className="text-lg font-semibold text-gray-900">Stage 2: Detailed Information</h3>
                  <CheckCircle className="h-5 w-5 text-green-600" />
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {/* Current School */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Current School</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><span className="font-medium">Name:</span> {applicationData.stage2.currentSchoolName}</p>
                      <p><span className="font-medium">Location:</span> {applicationData.stage2.currentSchoolCity}, {applicationData.stage2.currentSchoolState}</p>
                    </div>
                  </div>

                  {/* Parent Information */}
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Parent Information</h4>
                    <div className="space-y-2 text-sm text-gray-600">
                      <p><span className="font-medium">Parent 1:</span> {applicationData.stage2.parent1Name} ({applicationData.stage2.parent1Relationship})</p>
                      <p><span className="font-medium">Parent 2:</span> {applicationData.stage2.parent2Name} ({applicationData.stage2.parent2Relationship})</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Submission Notice */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
              <div className="flex items-start space-x-3">
                <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-2">Important Notice:</p>
                  <ul className="list-disc list-inside space-y-1">
                    <li>Please review all information carefully before submission</li>
                    <li>Once submitted, you cannot edit the application</li>
                    <li>Your application will be reviewed by the school administration</li>
                    <li>You will receive updates via email and SMS</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => navigate('/stage2')}
                className="flex-1 px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
              >
                Edit Stage 2
              </button>
              
              <button
                onClick={handleSubmit}
                disabled={submitting || !applicationData.stage2 || applicationData.payment?.status !== 'completed'}
                className="flex-1 flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </div>
                ) : (
                  <div className="flex items-center">
                    Submit Application
                    <ArrowRight className="h-4 w-4 ml-2" />
                  </div>
                )}
              </button>
            </div>

            {/* Submission Requirements */}
            {(!applicationData.stage2 || applicationData.payment?.status !== 'completed') && (
              <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start space-x-3">
                  <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div className="text-sm text-yellow-800">
                    <p className="font-medium mb-1">Submission Requirements:</p>
                    <ul className="list-disc list-inside space-y-1">
                      {!applicationData.stage2 && <li>Complete Stage 2 application form</li>}
                      {applicationData.payment?.status !== 'completed' && <li>Complete payment of {applicationFeeDisplay}</li>}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationReview;
